import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export type FinanceAccountType = "BANK" | "CASH" | "PARTNER_HOLDING";

export const ACCOUNT_TYPE_LABELS: Record<FinanceAccountType, string> = {
  BANK: "Bank",
  CASH: "Cash",
  PARTNER_HOLDING: "Partner holding",
};

export type FinanceAccount = {
  id: string;
  code: string;
  name: string;
  type: FinanceAccountType;
  currency: string;
  companyId: string;
  company: { id: string; code: string; name: string };
  partnerId: string | null;
  partner: { id: string; name: string; shortCode: string } | null;
  bankName: string | null;
  accountLast4: string | null;
  isActive: boolean;
  notes: string | null;
  balance: number;
};

export type AccountOptions = {
  companies: { id: string; code: string; name: string; currency: string }[];
  partners: { id: string; name: string; shortCode: string }[];
};

export type AccountPayload = {
  name?: string;
  type?: FinanceAccountType;
  companyId?: string;
  partnerId?: string | null;
  bankName?: string | null;
  accountLast4?: string | null;
  notes?: string | null;
  isActive?: boolean;
};

const ACCOUNTS_KEY = ["finance-accounts"] as const;

export function useFinanceAccounts(includeInactive: boolean): UseQueryResult<FinanceAccount[]> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, "list", includeInactive, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ accounts: FinanceAccount[] }>("/finance-accounts", {
        token: accessToken,
        query: { includeInactive: includeInactive ? "true" : undefined },
      });
      return res.accounts;
    },
  });
}

export function useAccountOptions(): UseQueryResult<AccountOptions> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, "options", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () => apiFetch<AccountOptions>("/finance-accounts/options", { token: accessToken }),
  });
}

export function useSaveAccount() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: AccountPayload }) =>
      apiFetch(id ? `/finance-accounts/${id}` : "/finance-accounts", {
        method: id ? "PUT" : "POST",
        token: accessToken,
        body: payload,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export type StatementLine = {
  id: string;
  code: string;
  entryDate: string;
  type: "OPENING_BALANCE" | "ADJUSTMENT";
  description: string;
  reason: string | null;
  partnerShortCode: string | null;
  sourceType: string | null;
  sourceId: string | null;
  reversalOfCode: string | null;
  reversedByCode: string | null;
  moneyIn: number;
  moneyOut: number;
  balance: number;
};

export type AccountStatement = {
  account: Omit<FinanceAccount, "balance">;
  from: string | null;
  to: string | null;
  hasOpeningBalance: boolean;
  openingBalance: number;
  totalIn: number;
  totalOut: number;
  closingBalance: number;
  lines: StatementLine[];
};

export function useAccountStatement(
  id: string,
  range: { from?: string; to?: string },
): UseQueryResult<AccountStatement> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, "statement", id, range, accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!id,
    queryFn: () =>
      apiFetch<AccountStatement>(`/finance-accounts/${id}/statement`, {
        token: accessToken,
        query: { from: range.from || undefined, to: range.to || undefined },
      }),
  });
}

export function useLedgerAction() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body }: { path: string; body: unknown }) =>
      apiFetch(`/finance-accounts/${path}`, { method: "POST", token: accessToken, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}
